import * as O from "fp-ts/Option"
import type { PlanLimitBannerProps } from "@/components/onboarding/PlanLimitBanner"
import { createOptionStore } from "@/lib/option-store"

// Le clavier reste piégé dans une fenêtre modale.
// Un toast y serait hors d'atteinte.
// L'écran de limite s'affiche donc dans la fenêtre.
// `PlanLimitDialogHost` le lit ici.

export type Hosted = {
  /** Fenêtre modale qui accueille l'écran. */
  container: HTMLElement
  title: string
  banner: PlanLimitBannerProps
}

export type HostedScreen = Hosted & { id: number }

const screenStore = createOptionStore<HostedScreen>()
// Numérote les écrans posés.
// React en remonte un neuf par refus.
const issued = { count: 0 }

export const subscribeHostedPlanLimit = screenStore.subscribe
export const hostedPlanLimit = screenStore.get

/**
 * Pose l'écran de limite dans `screen.container`.
 * Un refus plus récent remplace le précédent.
 *
 * @example hostPlanLimit({ container: dialog, title: "Limite du plan Gratuit atteinte", banner }) // l'écran s'affiche dans dialog
 */
export function hostPlanLimit(screen: Hosted): void {
  issued.count += 1
  screenStore.set(O.some({ ...screen, id: issued.count }))
}

/**
 * Retire l'écran posé dans la fenêtre.
 *
 * @example closeHostedPlanLimit() // PlanLimitDialogHost n'affiche plus rien
 */
export function closeHostedPlanLimit(): void {
  screenStore.set(O.none)
}

/**
 * Fenêtre modale ouverte, la plus récente.
 * Radix coupe les clics hors d'une fenêtre modale.
 * Les listes déroulantes et fenêtres sans piège ne comptent pas.
 *
 * @example
 * openModalDialog() // O.some(<div role="dialog" data-state="open">) sous « Nouveau programme »
 */
export function openModalDialog(): O.Option<HTMLElement> {
  if (document.body.style.pointerEvents !== "none") return O.none
  const dialogs = document.querySelectorAll<HTMLElement>(
    '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]',
  )
  return O.fromNullable(dialogs.item(dialogs.length - 1))
}
